import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell, CheckCircle, ChatCircleDots, FileText, Warning,
  UserPlus, ArrowRight, Checks
} from "@phosphor-icons/react";
import { asUtc, formatLocalDate } from "@/lib/timezone";

const typeIcons = {
  task_assigned: UserPlus,
  comment_added: ChatCircleDots,
  cr_status_changed: Warning,
  document_uploaded: FileText,
  survey_submitted: CheckCircle,
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get("/notifications?limit=20");
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.is_read).length);
    } catch {} finally { setLoading(false); }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await api.get("/notifications/unread-count");
      setUnreadCount(res.data.count);
    } catch {}
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      try { await api.put(`/notifications/${notif.id}/read`); } catch {}
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    if (notif.link) navigate(notif.link);
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    try { await api.put("/notifications/read-all"); } catch {}
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleToggle = () => {
    if (!open) loadNotifications();
    setOpen(!open);
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    // Session 68 — Bug 6: notification timestamps used to flip from
    // relative ("13m ago") to a numeric date ("5/4/2026") at the
    // 24-hour mark. Two issues caused that:
    //   1. The threshold was tight (24h) so the formats sat side by
    //      side in the same dropdown.
    //   2. `new Date(iso)` was parsing SQL Server's naive ISO as
    //      browser-local time, so the relative offset was wrong by
    //      the viewer's UTC delta.
    // Fix: extend the relative ladder to 7 days (so "Nm/Nh/Nd ago"
    // is the dominant format), then switch to a clean tz-aware
    // absolute date for older items. `asUtc()` normalises the naive
    // ISO before subtracting.
    const ts = Date.parse(asUtc(iso));
    if (Number.isNaN(ts)) return "";
    const diffMs = Date.now() - ts;
    if (diffMs < 60_000) return "Just now";
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
    if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
    if (diffMs < 7 * 86_400_000) return `${Math.floor(diffMs / 86_400_000)}d ago`;
    return formatLocalDate(iso);
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell-container">
      <button
        onClick={handleToggle}
        className="relative p-2 hover:bg-[#F5F5F5] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} weight={unreadCount > 0 ? "fill" : "regular"} className="text-[#0A0A0A]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-[#E5E5E5] shadow-lg z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline flex items-center gap-1"
                data-testid="mark-all-read-button"
              >
                <Checks size={12} /> Mark all read
              </button>
            )}
          </div>

          <ScrollArea className="max-h-[360px]">
            {loading && notifications.length === 0 ? (
              <div className="p-6 text-center"><div className="w-5 h-5 border-2 border-[#0055FF] border-t-transparent animate-spin mx-auto" /></div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#A3A3A3]">No notifications yet</div>
            ) : (
              <div>
                {notifications.map(notif => {
                  const IconComp = typeIcons[notif.type] || Bell;
                  return (
                    <button
                      key={notif.id}
                      onClick={() => handleClick(notif)}
                      className={`w-full text-left px-4 py-3 border-b border-[#F5F5F5] hover:bg-[#FAFAFA] transition-colors flex gap-3 ${
                        !notif.is_read ? "bg-[#F0F7FF]" : ""
                      }`}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        !notif.is_read ? "bg-[#0055FF]/10" : "bg-[#F5F5F5]"
                      }`}>
                        <IconComp size={14} className={!notif.is_read ? "text-[#0055FF]" : "text-[#A3A3A3]"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-snug ${!notif.is_read ? "font-semibold text-[#0A0A0A]" : "text-[#525252]"}`}>
                          {notif.title}
                        </p>
                        <p className="text-[10px] text-[#A3A3A3] mt-0.5 truncate">{notif.message}</p>
                        <p className="text-[10px] text-[#A3A3A3] mt-0.5">{formatTime(notif.created_at)}</p>
                      </div>
                      {!notif.is_read && <div className="w-2 h-2 bg-[#0055FF] flex-shrink-0 mt-2" />}
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
